import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiTablet, FiHeadphones, FiWatch, FiBattery, FiShield } = FiIcons;

const Services = () => {
  const productImages = [
    "https://i.ibb.co/gFbg9cYT/image-1.jpg",
    "https://i.ibb.co/k2ShZ4qz/image-1.png",
    "https://i.ibb.co/Q3BxmfY7/image-2.jpg",
    "https://i.ibb.co/tMf2yN33/image-3.jpg",
    "https://i.ibb.co/SDnt4nL3/image-4.jpg",
    "https://i.ibb.co/3y1Z83SY/image-5.jpg",
    "https://i.ibb.co/ndfjy3m/image-6.jpg",
    "https://i.ibb.co/BVq4Z9yr/image-7.jpg",
    "https://i.ibb.co/XfVPJnLx/image-8.jpg",
    "https://i.ibb.co/7tx7nDbf/image-9.jpg",
    "https://i.ibb.co/hJnBXJDJ/image-10.jpg",
    "https://i.ibb.co/DPVg5bNK/image-11.jpg",
    "https://i.ibb.co/LhbHqVXt/image-12.jpg",
    "https://i.ibb.co/9C0RwGN/image-13.jpg",
    "https://i.ibb.co/hRGNGmWt/image-14.jpg",
    "https://i.ibb.co/9B3tfVx/image-15.jpg",
    "https://i.ibb.co/4R9KKywG/image-16.jpg",
    "https://i.ibb.co/kggKhpjs/image-17.jpg",
    "https://i.ibb.co/xqqdkKw4/image-18.jpg",
    "https://i.ibb.co/kg3mftqr/image-19.jpg",
    "https://i.ibb.co/rGpxNgsg/image-20.jpg",
    "https://i.ibb.co/Fb8hvF5Y/image-21.jpg",
    "https://i.ibb.co/yCjNJVh/image-22.jpg",
    "https://i.ibb.co/v4Nfs0zF/image-23.jpg"
  ];

  const services = [
    {
      icon: FiPhone,
      title: "Mobile Phones",
      description: "Latest smartphones from top brands including iPhone, Samsung, Huawei, and more",
      features: ["Latest Models", "Warranty Included", "Competitive Prices", "Trade-in Options"]
    },
    {
      icon: FiTablet,
      title: "Tablets & iPads",
      description: "High-performance tablets for work, entertainment, and creativity",
      features: ["All Screen Sizes", "WiFi & Cellular", "Accessories Available", "Expert Setup"]
    },
    {
      icon: FiHeadphones,
      title: "Audio Accessories",
      description: "Premium headphones, earbuds, speakers, and audio equipment",
      features: ["Wireless & Wired", "Noise Cancellation", "Premium Brands", "Sound Testing"]
    },
    {
      icon: FiWatch,
      title: "Smart Watches",
      description: "Fitness trackers and smartwatches for health and connectivity",
      features: ["Health Monitoring", "GPS Tracking", "Water Resistant", "Long Battery Life"]
    },
    {
      icon: FiBattery,
      title: "Charging Solutions",
      description: "Fast chargers, power banks, wireless charging pads and cables",
      features: ["Fast Charging", "Multiple Ports", "Portable Design", "Safety Certified"]
    },
    {
      icon: FiShield,
      title: "Protection & Cases",
      description: "Screen protectors, cases, and covers to keep your devices safe",
      features: ["Tempered Glass", "Shock Protection", "Custom Fit", "Style Options"]
    }
  ];

  return (
    <div className="w-full overflow-x-hidden">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-600 via-red-700 to-red-800 text-white py-20 w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Our Products & Services
            </h1>
            <p className="text-xl text-red-100 max-w-3xl mx-auto leading-relaxed">
              Discover our comprehensive range of mobile phones, electronics, and accessories. 
              We offer the latest technology from trusted brands at competitive prices.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 w-full">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow group"
              >
                <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:bg-red-600 transition-colors">
                  <SafeIcon icon={service.icon} className="w-8 h-8 text-red-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-600">
                      <div className="w-2 h-2 bg-red-600 rounded-full mr-3 flex-shrink-0"></div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Gallery */}
      <section className="py-20 bg-gray-50 w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Product Gallery
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Browse through our extensive collection of mobile phones, electronics, and accessories
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 w-full">
            {productImages.map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="relative group cursor-pointer overflow-hidden rounded-lg shadow-lg"
              >
                <img
                  src={image}
                  alt={`Product ${index + 1}`}
                  className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Services?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We provide exceptional service and quality products to ensure customer satisfaction
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 w-full">
            {[
              {
                title: "Quality Guarantee",
                description: "All products come with manufacturer warranty and our quality assurance"
              },
              {
                title: "Expert Advice",
                description: "Our knowledgeable staff helps you choose the right product for your needs"
              },
              {
                title: "Competitive Prices",
                description: "Best prices in Dubai with regular offers and discounts for customers"
              },
              {
                title: "After-Sales Support",
                description: "Comprehensive support including setup, troubleshooting, and maintenance"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center p-6"
              >
                <div className="bg-red-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  {index + 1}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-600">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-red-600 text-white w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <h2 className="text-3xl md:text-4xl font-bold">
              Need Help Choosing the Right Product?
            </h2>
            <p className="text-xl text-red-100 max-w-3xl mx-auto">
              Our expert team is here to help you find the perfect device that meets your needs and budget.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+971503598111"
                className="bg-white text-red-600 px-8 py-3 rounded-lg font-semibold hover:bg-red-50 transition-colors"
              >
                Call Us Now
              </a>
              <a
                href="https://maps.app.goo.gl/DBLRAmgU6RETSxxH7"
                target="_blank"
                rel="noopener noreferrer"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-red-600 transition-colors"
              >
                Visit Our Store
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;